import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ShowPerformance } from '../../models/showperformance.model';
import { AlertController, Platform, ToastController } from '@ionic/angular';
import { DocumentService } from '../document.service';
import { CARODocument, CaroDocumentType } from '../../models/document.model';
import { DocumentConverter } from '../document-converter';

@Component({
    selector: 'app-uploadfield',
    templateUrl: './upload-field.component.html',
    styleUrls: ['./upload-field.component.scss'],
    standalone: false
})
export class UploadFieldComponent implements OnInit {
    @Input() public performance: ShowPerformance;
    @Input() public title = 'Upload';
    @Input() public documentType = CaroDocumentType.eftelingshowreport;
    @Input() public showDeleteButton = false;
    @Input() public isFileUploaded = false;
    @Input() public caroDocument: CARODocument | null = null;

    @Output() public fileUploaded = new EventEmitter<boolean>();
    @Output() public fileDeleted = new EventEmitter<boolean>();

    public buffer = 0;
    public progress = 0;
    public downloadButtonText: string;

    public platform: Platform = inject(Platform);

    private alertController: AlertController = inject(AlertController);
    private toastController: ToastController = inject(ToastController);
    private pdfString: string;
    private documentService: DocumentService = inject(DocumentService);

    private documentConverter: DocumentConverter = inject(DocumentConverter);

    constructor() {
    }

    ngOnInit(): void {
        switch (this.documentType) {
            case CaroDocumentType.callsheet:
                this.downloadButtonText = 'Callsheet';
                break;
            case CaroDocumentType.eftelingshowreport:
                this.downloadButtonText = 'Efteling Showreport';
                break;
            case CaroDocumentType.protocol:
                this.downloadButtonText = 'Protocol';
                break;
            default:
                this.downloadButtonText = 'Download';
                break;
        }
    }

    public async handleSelectedFile($event) {
        await this.handleFileConversionAndUpload($event.target.files[0]);
    }

    public async downloadDocument() {
        throw new Error('This method has been depricated');
    }

    public async handleFileConversionAndUpload($event: any): Promise<void> {
        await this.setProgressBarToPercentage(10);

        const file: File = $event;

        console.log(
            'APP [UploadFieldComponent] handleFileConversionAndUpload() - file: ',
            file
        );

        // Cast file as HTML File

        // 1. Check of we need $event[0] or $event.target.files[0]
        await this.setProgressBarToPercentage(20);

        // 2. Check if the file is a PDF
        if (!this.isFileCorrectType(file)) {
            this.displayWrongFileTypeAlert();
            return;
        }
        await this.setProgressBarToPercentage(20);

        // 3. Convert the PDF to a base64 string

        const uploadedFileName =            this.performance?.show?.name +            ' - ' +            this.documentType.toString() +            ' ' +            this.getFormattedDate();
        const base64String = await this.convertPDFToBase64String(file);
        await this.setProgressBarToPercentage(60);

        // 4. Upload the base64 string to the database
        const isFileUploaded = await this.uploadBase64StringToDatabase(
            base64String,
            uploadedFileName
        );

        await this.setProgressBarToPercentage(80);

        if (isFileUploaded) {
            await this.setProgressBarToPercentage(99);

            // Emit an event to the parent component to let it know that the file has been uploaded
            this.fileUploaded.emit(true);
        } else {
            // Emit an event to the parent component to let it know that the file has been uploaded
            this.fileUploaded.emit(false);
            await this.setProgressBarToPercentage(0);

            this.toastController
                .create({
                    message:
                        'Er is iets misgegaan bij het uploaden van het bestand. Probeer het later nog eens.',
                    duration: 5000,
                    color: 'danger',
                    position: 'bottom'
                })
                .then(toast => {
                    toast.present();
                });
        }
    }

    public getFileReader(): FileReader {
        const fileReader = new FileReader();
        const zoneOriginalInstance = (fileReader as any)[
            // eslint-disable-next-line @typescript-eslint/dot-notation
            '__zone_symbol__originalInstance'
            ];
        return zoneOriginalInstance || fileReader;
    }

    public async deleteDocument(): Promise<void> {
        this.isFileUploaded = false;

        await this.setProgressBarToPercentage(60);

        const isFileDeleted = await this.callDeleteDocumentService();

        await this.setProgressBarToPercentage(30);

        // Wait for the file to be deleted
        await this.delay(1000);

        if (isFileDeleted) {
            await this.setProgressBarToPercentage(0);

            this.fileDeleted.emit(true);
        } else {
            this.fileDeleted.emit(false);
            await this.setProgressBarToPercentage(0);

            this.toastController
                .create({
                    message:
                        'Er is iets misgegaan bij het uploaden van het bestand. Probeer het later nog eens.',
                    duration: 5000,
                    color: 'danger',
                    position: 'bottom'
                })
                .then(toast => {
                    toast.present();
                });
        }
    }

    private padNumber(num: number): string {
        return num.toString().padStart(2, '0');
    }

    private async setProgressBarToPercentage(percentage: number) {
        await this.delay(200);
        this.progress = percentage / 100;
        this.buffer = this.progress + 0.1;
    }

    private async handleFileUpload(reader: FileReader) {
        console.log('[UploadFieldComponent] handleFileUpload()');
        await this.setProgressBarToPercentage(40);
        this.pdfString = reader.result.toString();
        this.addFileToPerformance();
    }

    private addFileToPerformance() {
        console.log('[UploadFieldComponent] addFileToPerformance()');
        this.performance.eftelingShowreport = this.pdfString;
        // TODO: Use the service to update the performance in the database
    }

    private async delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private isFileCorrectType(uploadedFile: any): boolean {
        console.log('[UploadFieldComponent] isFileCorrectType()');
        return uploadedFile.type === 'application/pdf';
    }

    private displayWrongFileTypeAlert() {
        this.alertController
            .create({
                header: 'Verkeerd bestandtype',
                message:
                    'Het geselecteerde bestand is geen PDF. Alleen PDF is toegestaan.',
                buttons: ['OK']
            })
            .then(alert => {
                alert.present().then(r => console.log('Bestand is geen PDF'));
            });
    }

    private async checkEvent($event: any): Promise<File> {
        console.log('[UploadFieldComponent] checkEvent(), $event: ', $event);
        if ($event.target.files) {
            return $event.target.files[0];
        }
            return $event[0];

    }

    private getFormattedDate(): string {
        let date: Date;
        if (this.performance?.datetime) {
            date = new Date(this.performance.datetime);
        } else {
            date = new Date();
        }
        const dateToReturn = `${ this.padNumber(date.getDate()) }-${ this.padNumber(
            date.getMonth() + 1
        ) }-${ date.getFullYear() }`;
        console.log(
            'APP [UploadFieldComponent] getFormattedDate() - dateToReturn: ',
            dateToReturn
        );
        return dateToReturn;
    }

    private convertBase64ToBlobData(source: string, applicationPdf: string) {
        const byteCharacters = atob(
            source.replace(/^data:application\/pdf;base64,/, '')
        );
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {type: applicationPdf});
        return blob;
    }

    private async convertPDFToBase64String(file: any): Promise<string> {
        console.log('[UploadFieldComponent] convertPDFToBase64String()');
        await this.setProgressBarToPercentage(30);
        if (!this.platform.is('android') && !this.platform.is('ios')) {
            console.log(
                'APP [UploadFieldComponent] convertPDFToBase64String() - is desktop'
            );
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result.toString());
                reader.onerror = error => reject(error);
            });
        }
            return new Promise((resolve, reject) => {
                const reader = this.getFileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result.toString());
                reader.onerror = error => reject(error);
            });

    }

    private async callDeleteDocumentService(): Promise<boolean> {
        const response = await this.documentService.delete(this.caroDocument.id);
        console.log(
            '[UploadFieldComponent] uploadBase64StringToDatabase() response: ',
            response
        );

        // return response.message === "Document uploaded successfully";
        return true;
    }

    private async uploadBase64StringToDatabase(
        base64String: string,
        documentName: string
    ): Promise<boolean> {
        console.log('[UploadFieldComponent] uploadBase64StringToDatabase()');
        console.log(
            'APP [UploadFieldComponent] uploadBase64StringToDatabase() - this.performance.id:',
            this.performance?.id
        );
        await this.setProgressBarToPercentage(50);
        console.log(
            'APP [UploadFieldComponent] uploadBase64StringToDatabase() - documentType: ' +            this.documentType
        );
        const response = await this.documentService.uploadDocument(
            base64String,
            this.performance?.id || null,
            this.documentType,
            documentName
        );
        console.log(
            '[UploadFieldComponent] uploadBase64StringToDatabase() response: ',
            response
        );

        // return response.message === "Document uploaded successfully";
        return true;
    }
}
